/**
 * ORALIANE two-step watch selector (design spec 5.2).
 *
 * Works together with the rewritten snippets/product-variant-picker.liquid.
 * Dawn's product-info.js re-renders the buy box on every option change, so
 * all event wiring is delegated on `document` and UI state is re-applied
 * ("sanitized") after each swap. The only state that must survive a swap —
 * whether the customer explicitly confirmed a watch model — lives at module
 * scope, because the server always re-renders one radio as checked.
 *
 * Guards:
 * - Step 2 stays collapsed until a case family is chosen, and its exact-model
 *   choice only counts once the customer clicks it.
 * - The server always pre-checks the first available variant (direct entry and
 *   ?variant= deep links alike): that pre-selection may pick the FINISH, but it
 *   must never open step 2 or count as a confirmed model — sanitize() clears it.
 * - Switching family clears the previous exact-model choice (spec 5.2).
 * - Add to bag with no explicit model choice shows an error (role=alert)
 *   and scrolls back to the case selector; a wrong-model order is worse
 *   than an extra click (spec 5.2, spec 10 size-error metric).
 * - The mobile sticky buy bar stays hidden while the real buy box is on
 *   screen; once it scrolls out of view the bar slides up. Its label mirrors
 *   the two-step picker: "Choose options" scrolls back to the buy box,
 *   "Add to cart" (after an explicit model choice) submits the real product
 *   form so Dawn's own flow — loading state, cart drawer, guards — runs.
 *   Standard products without the two-step picker go straight to "Add to
 *   cart" mode.
 * - Fit gate: every checkout entry point (add-to-cart submit button, Buy it
 *   now / more-payment-options, installment widget) is rendered LOCKED by the
 *   server (buy-buttons.liquid / main-product.liquid) and only this script's
 *   applyGate() unlocks it once an exact model is confirmed. If JS never
 *   runs, the product cannot be bought with a guessed variant — a missed
 *   sale costs less than a wrong-model order (spec 5.2, spec 10).
 * - Required size choice in EVERY picker mode (2026-09-09): the server always
 *   preselects the first available variant, but that preselection never counts
 *   as a choice. On one-step pages (variant-selects[data-oraliane-requires-
 *   choice] without the two-step fieldset) sanitize() clears the flat radios
 *   or resets the dropdown to its "Select your size" placeholder until a
 *   trusted change confirms a size — same doctrine as the two-step flow.
 */

(function () {
  'use strict';

  var modelChosen = false;
  var activeFamily = null;

  /* Static server-rendered nodes: the sticky bar sits outside product-info
     and survives Dawn's re-renders, and so does the buy box's buttons node
     (product-info.js only swaps price innerHTML / input values in place). */
  var stickyBar = null;
  var stickyPrice = null;

  function familyOf(input) {
    var mm = parseInt(input && input.dataset.oralianeMm, 10);
    if (!mm) return null;
    return mm <= 42 ? 'small' : 'large';
  }

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function setActiveFamily(root, family) {
    var caseFieldset = q(root, '[data-oraliane-case]');
    var modelFieldset = q(root, '[data-oraliane-models]');
    if (!caseFieldset || !modelFieldset) return;

    caseFieldset.querySelectorAll('[data-oraliane-case-family]').forEach(function (tab) {
      var active = tab.dataset.oralianeCaseFamily === family;
      tab.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    modelFieldset.disabled = !family;

    modelFieldset.querySelectorAll('[data-oraliane-model-group]').forEach(function (group) {
      group.hidden = group.dataset.oralianeModelGroup !== family;
    });
  }

  function clearModelChoice(root) {
    root.querySelectorAll('[data-oraliane-models] input[type="radio"]').forEach(function (input) {
      input.checked = false;
    });
  }

  function showError(root) {
    var error = q(root, '[data-oraliane-fit-error]');
    if (!error) return;
    error.hidden = false;
    var caseFieldset = q(root, '[data-oraliane-case]');
    if (caseFieldset) {
      caseFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var firstTab = q(caseFieldset, '[data-oraliane-case-family]');
      if (firstTab) firstTab.focus({ preventScroll: true });
      return;
    }
    /* One-step pages: scroll to the size control itself. */
    var flat = q(root, '[data-oraliane-flat]');
    var select = flat ? null : caseSelect(root);
    var target = flat || select;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (select) select.focus({ preventScroll: true });
    }
  }

  function hideError(root) {
    var error = q(root, '[data-oraliane-fit-error]');
    if (error) error.hidden = true;
  }

  /* Mobile swatch rows scroll horizontally; a Dawn re-render rebuilds the row
     at scroll position 0, which can leave the freshly selected card out of
     view. Bring it back to the row's leading edge. Only the row's own
     scrollLeft moves — the page never jumps. */
  function alignSelectedSwatch(scope) {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scope.querySelectorAll('.oraliane-swatches').forEach(function (row) {
      var checked = row.querySelector('input[type="radio"]:checked');
      var card = checked && row.querySelector('label[for="' + checked.id + '"]');
      if (!card) return;
      var delta = card.getBoundingClientRect().left - row.getBoundingClientRect().left;
      var target = Math.min(row.scrollLeft + delta, row.scrollWidth - row.clientWidth);
      if (row.scrollWidth <= row.clientWidth || Math.abs(row.scrollLeft - target) < 1) return;
      row.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ---- one-step required choice (flat fieldset / dropdown placeholder) ---- */

  function requiresChoiceRoot() {
    return q(document, 'variant-selects[data-oraliane-requires-choice]');
  }

  function caseSelect(root) {
    var placeholder = q(root, '[data-oraliane-placeholder]');
    return placeholder ? placeholder.closest('select') : null;
  }

  function clearOneStepPreselection(root) {
    /* The server render preselects the first available size (flat: a checked
       radio; dropdown: the option[selected] attribute). Neither counts as a
       choice: uncheck the radios and point the select at its placeholder.
       Programmatic .value assignment fires no change event, so Dawn's own
       flow stays idle, and the option[selected] attribute — the only thing
       Dawn's URL building reads — is left untouched on the real option. */
    root.querySelectorAll('[data-oraliane-flat] input[type="radio"]').forEach(function (input) {
      input.checked = false;
    });
    var select = caseSelect(root);
    if (select) select.value = '';
  }

  function applyOneStepChoice(root) {
    var note = q(root, '[data-oraliane-choice-note]');
    if (note) note.hidden = modelChosen;
    if (modelChosen) {
      /* Confirmed: retire the placeholder so Dawn's re-renders never show it
         in the open option list again. */
      root.querySelectorAll('[data-oraliane-placeholder]').forEach(function (option) {
        option.remove();
      });
    }
  }

  /* Re-apply UI state to freshly swapped markup. Called on init and after
     every product-info re-render. */
  function sanitize(root) {
    var modelFieldset = q(root, '[data-oraliane-models]');
    var oneStep = q(root, 'variant-selects[data-oraliane-requires-choice]');
    if (!modelFieldset) {
      if (!oneStep) {
        /* No two-step picker AND no required-choice marker (non-ORALIANE
           product, or the merchant removed the picker block): lift the gate
           so a product is never left unpurchasable behind a selector that
           isn't there. No-op on pages without gated buy boxes. */
        liftGate();
        alignSelectedSwatch(root);
        return;
      }
      /* One-step page: enforce the required size choice. */
      if (!modelChosen) clearOneStepPreselection(oneStep);
      applyOneStepChoice(oneStep);
      hideError(oneStep);
      applyGate();
      syncStickyBar();
      alignSelectedSwatch(root);
      return;
    }

    if (modelChosen) {
      /* The customer explicitly confirmed a model: restore the expanded UI.
         The server re-render pre-checks the confirmed variant's radio. */
      var confirmed = q(modelFieldset, 'input[type="radio"]:checked');
      if (confirmed) activeFamily = familyOf(confirmed) || activeFamily;
      modelFieldset.disabled = false;
      setActiveFamily(root, activeFamily || 'small');
    } else {
      /* No explicit model choice yet. The server render always pre-checks the
         first available variant (direct entry and ?variant= deep links alike) —
         that pre-selection must not open step 2 or count as a confirmation. */
      clearModelChoice(root);
      modelFieldset.disabled = !activeFamily;
      setActiveFamily(root, activeFamily);
    }
    syncStickyBar();
    applyGate();
    alignSelectedSwatch(root);
  }

  /* ---- fit gate: buy actions locked until an exact model is confirmed ---- */

  function gatedForms() {
    return document.querySelectorAll('product-form[data-oraliane-gate]');
  }

  /* Availability of the currently selected variant, from the JSON payload
     Dawn re-renders inside variant-selects. Treat unknown as available and
     let Dawn's own sold-out flow handle it. */
  function selectedVariantAvailable() {
    var script = q(document, 'variant-selects [data-selected-variant]');
    if (!script) return true;
    try {
      var variant = JSON.parse(script.textContent);
      return !variant || variant.available !== false;
    } catch (error) {
      return true;
    }
  }

  function setInert(gates, locked) {
    gates.forEach(function (gate) {
      if (locked) {
        gate.setAttribute('inert', '');
      } else {
        gate.removeAttribute('inert');
      }
    });
  }

  /* Locked = no confirmed model. The submit button is disabled with its
     normal label (Dawn's variant-change sync may call it "Sold out" while
     the real reason is the unconfirmed fit); the dynamic-checkout wrapper
     and the installment widget get inert, which blocks mouse, keyboard and
     assistive tech even when Shopify's payment script re-renders its own
     buttons inside. Unlock keeps Dawn's availability handling: a sold-out
     variant stays disabled. */
  function applyGate() {
    var locked = !modelChosen;

    gatedForms().forEach(function (form) {
      form.toggleAttribute('data-oraliane-fit-locked', locked);
      var submit = q(form, 'button[name="add"]');
      if (locked) {
        if (submit) {
          submit.setAttribute('disabled', 'disabled');
          var label = q(submit, 'span');
          if (label && window.variantStrings && window.variantStrings.addToCart) {
            label.textContent = window.variantStrings.addToCart;
          }
        }
      } else if (submit && selectedVariantAvailable()) {
        submit.removeAttribute('disabled');
      }
      setInert(form.querySelectorAll('[data-oraliane-payment]'), locked);
    });

    /* The installment widget lives in the price block, outside any
       product-form, and checks out with its hidden variant id directly. */
    setInert(document.querySelectorAll('[data-oraliane-payment-terms]'), locked);
  }

  function liftGate() {
    gatedForms().forEach(function (form) {
      form.removeAttribute('data-oraliane-gate');
      form.removeAttribute('data-oraliane-fit-locked');
      setInert(form.querySelectorAll('[data-oraliane-payment]'), false);
      var submit = q(form, 'button[name="add"]');
      if (submit && selectedVariantAvailable()) submit.removeAttribute('disabled');
    });
    setInert(document.querySelectorAll('[data-oraliane-payment-terms]'), false);
  }

  /* ---- mobile sticky buy bar (spec 7) ---- */

  /* Mirror the picker state onto the bar: label + live price. Called on
     init, on option change, and after every product-info re-render (Dawn
     swaps #price-{section} innerHTML, so the bar copies the fresh price).
     The two-step picker ("Choose options" → confirm model) is the only path
     that needs an explicit confirmation; a standard product goes straight
     to "Add to cart". */
  function stickyAddMode() {
    return modelChosen || !requiresChoiceRoot();
  }

  function syncStickyBar() {
    if (!stickyBar) return;
    var addMode = stickyAddMode();
    stickyBar.dataset.oralianeMobileAtcMode = addMode ? 'add' : 'choose';

    var chooseLabel = q(stickyBar, '[data-oraliane-mobile-atc-label-choose]');
    var addLabel = q(stickyBar, '[data-oraliane-mobile-atc-label-add]');
    if (chooseLabel) chooseLabel.hidden = addMode;
    if (addLabel) addLabel.hidden = !addMode;

    if (stickyPrice) {
      var priceRoot = document.querySelector('product-info .price--large');
      var onSale = priceRoot && priceRoot.classList.contains('price--on-sale');
      var amount =
        priceRoot &&
        priceRoot.querySelector(
          onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'
        );
      if (amount) stickyPrice.textContent = amount.textContent.trim();
    }
  }

  /* Reveal the bar only after the buy box has been SCROLLED PAST — its bottom
     edge above the viewport top. A plain "not intersecting" test is wrong on
     mobile: at page load the buy box sits below the fold (also not
     intersecting), which would pop the bar immediately on entry. */
  function setupStickyBar() {
    stickyBar = document.querySelector('[data-oraliane-mobile-atc]');
    if (!stickyBar) return;
    stickyPrice = q(stickyBar, '[data-oraliane-mobile-atc-price]');
    syncStickyBar();

    var buyArea = document.querySelector('.product-form__buttons');
    if (!buyArea || typeof IntersectionObserver !== 'function') return;

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      var scrolledPast = entry.boundingClientRect.bottom <= 0;
      stickyBar.classList.toggle('oraliane-mobile-atc--visible', scrolledPast);
    });
    observer.observe(buyArea);
  }

  /* Returns true when the add actually started; false falls back to
     scrolling (e.g. sold-out variant, submit button disabled). */
  function submitFromStickyBar() {
    var form = document.querySelector('form[data-type="add-to-cart-form"]');
    var submitButton = form && form.querySelector('[name="add"]');
    if (!submitButton || submitButton.disabled) return false;
    form.requestSubmit(submitButton);
    return true;
  }

  function findRoot(node) {
    return node && node.closest ? node.closest('variant-selects') : null;
  }

  /* ---- delegated events (survive Dawn's DOM swaps) ---- */

  document.addEventListener('click', function (event) {
    var root = findRoot(event.target);

    // Case family tab
    var tab = event.target.closest ? event.target.closest('[data-oraliane-case-family]') : null;
    if (tab && root) {
      var switchingFamily = q(root, '[data-oraliane-case-family][aria-pressed="true"]');
      if (switchingFamily && switchingFamily !== tab) {
        // Switching family invalidates the exact-model choice (spec 5.2)
        modelChosen = false;
        clearModelChoice(root);
        hideError(root);
        applyGate();
      }
      activeFamily = tab.dataset.oralianeCaseFamily;
      setActiveFamily(root, activeFamily);
      syncStickyBar();
      return;
    }

    // Size help dialog
    if (event.target.closest('[data-oraliane-open-size-help]')) {
      var dialog = root && q(root, '[data-oraliane-size-dialog]');
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    if (event.target.closest('[data-oraliane-close-size-help]')) {
      var dialogToClose = event.target.closest('dialog');
      if (dialogToClose) dialogToClose.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement) {
      // Backdrop click closes
      var rect = event.target.getBoundingClientRect();
      var inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) event.target.close();
      return;
    }

    // Mobile sticky buy bar: "Add to cart" submits the real form; "Choose
    // options" smooth-scrolls back to the buy box
    var atcLink = event.target.closest ? event.target.closest('[data-oraliane-mobile-atc-link]') : null;
    if (atcLink) {
      event.preventDefault();
      var bar = atcLink.closest('[data-oraliane-mobile-atc]');
      if (!(bar && bar.dataset.oralianeMobileAtcMode === 'add' && submitFromStickyBar())) {
        var target = document.querySelector(atcLink.getAttribute('href'));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  document.addEventListener('change', function (event) {
    var root = findRoot(event.target);
    if (!root) return;

    var isModel = event.target.closest('[data-oraliane-models]');
    if (isModel && event.target.type === 'radio' && event.isTrusted) {
      modelChosen = true;
      hideError(root);
    }
    /* One-step pages: a trusted change on the flat fieldset or on the gated
       case select confirms the size. The placeholder carries value="" and is
       disabled, so a non-empty select value is always a real choice. */
    if (!modelChosen && root.hasAttribute('data-oraliane-requires-choice')) {
      var flatChoice = event.target.closest('[data-oraliane-flat]');
      var gatedSelect = caseSelect(root);
      if (
        (flatChoice && event.target.type === 'radio') ||
        (gatedSelect && event.target === gatedSelect && event.target.value !== '')
      ) {
        modelChosen = true;
        applyOneStepChoice(root);
        hideError(root);
      }
    }
    /* Sync immediately — Dawn's section re-render (and its sanitize pass)
       only lands after the network round-trip. */
    syncStickyBar();
    applyGate();
  });

  /* Capture phase: must run before Dawn's product-form handler, which binds
     directly on the form and would otherwise start the cart add. Scope: the
     real add-to-cart form (the variant picker lives OUTSIDE it as a sibling
     block, so the form never contains variant-selects — look it up on
     document, not on the form). */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form.querySelector || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      if (!requiresChoiceRoot()) return;
      if (!modelChosen) {
        event.preventDefault();
        event.stopPropagation();
        showError(requiresChoiceRoot());
      }
    },
    true
  );

  /* ---- re-apply state after Dawn re-renders the buy box ---- */

  var frame = null;
  var observer = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes).some(function (node) {
        return node.nodeType === 1 && (node.matches('variant-selects') || node.querySelector('variant-selects'));
      });
    });
    if (!relevant || frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      sanitize(document);
    });
  });

  function init() {
    observer.observe(document.body, { childList: true, subtree: true });
    setupStickyBar();
    sanitize(document);
    /* Dawn's option-change re-render resolves asynchronously (network fetch
       + view transition), so the MutationObserver alone can race it and the
       button sync inside product-info.js runs after the DOM swap. Re-apply
       the full UI state once Dawn's own variantChange subscribers finish. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        sanitize(document);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
